import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { AUTOFILL_IGNORE_PROPS } from '../../lib/autofill';

// Input types that never get a password-manager overlay (or, for `password`, deliberately want one) —
// same exclusions as the `terra/input-autofill-ignore` lint rule.
const NON_TEXT_TYPES = new Set(['checkbox', 'radio', 'range', 'file', 'hidden', 'submit', 'button', 'reset', 'image', 'color', 'password']);

const ATTRIBUTES: ReadonlyArray<readonly [string, string]> = [
  ['autocomplete', AUTOFILL_IGNORE_PROPS.autoComplete],
  ['data-form-type', AUTOFILL_IGNORE_PROPS['data-form-type']],
  ['data-1p-ignore', AUTOFILL_IGNORE_PROPS['data-1p-ignore']],
  ['data-lpignore', AUTOFILL_IGNORE_PROPS['data-lpignore']],
  ['data-bwignore', AUTOFILL_IGNORE_PROPS['data-bwignore']],
];

function stampField(el: Element) {
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
  if (el instanceof HTMLInputElement && NON_TEXT_TYPES.has(el.type)) return;
  // Idempotent: setAttribute with the same value is a no-op, so re-running is safe.
  for (const [name, value] of ATTRIBUTES) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }
}

function stamp(root: HTMLElement) {
  Array.from(root.querySelectorAll('input, textarea')).forEach(stampField);
}

/**
 * The sanctioned way to opt a CLOSED Glaze component out of password-manager autofill icons —
 * one that renders its own `<input>` and does not forward arbitrary props to it (e.g.
 * `DebouncedSearchInput`, `Combobox`). Where a component does forward props (`InputField`,
 * `TextareaField`), spread `{...AUTOFILL_IGNORE_PROPS}` at the call site instead.
 *
 * Renders a `display: contents` box (no layout effect) and stamps the shared attribute bag on every
 * descendant text-like input/textarea: after each commit, and again via a `MutationObserver` for
 * inputs a child mounts later without re-rendering this wrapper. Safe under StrictMode (the effect
 * only sets attributes and disconnects its observer on cleanup).
 *
 * Portaled content (a popover `Combobox`'s search box) is NOT a DOM descendant, so the scan above
 * can't see it; React still bubbles its focus events up the React tree, so `onFocusCapture` stamps
 * the field the moment it takes focus. That is later than a mount-time scan, but it is the only
 * hook a wrapper has without reaching into `document.body`.
 */
export function AutofillIgnore({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  // No deps: `children` is a fresh element every render, so re-stamp after each commit.
  useLayoutEffect(() => {
    if (ref.current) stamp(ref.current);
  });

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const observer = new MutationObserver(() => stamp(root));
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="contents" onFocusCapture={e => stampField(e.target as Element)}>
      {children}
    </div>
  );
}
